import { QueryClient } from "react-query"

import { checkAuth, confirmEmail } from "api/sessions"
import { authToken } from "services/authentication"
import { setCurrentUser } from "context/actions"
import { errorToast, successToast } from "utilities/toasts"
import { User } from "sharedTypes"

type ConfirmEmailTokenResult = {
  data: User
  priorConfirmed: boolean
}

const confirmEmailTokenAndToast = async (
  {
    confirmationToken,
    queryClient,
  }: {
    confirmationToken: string
    queryClient: QueryClient
  },
): Promise<ConfirmEmailTokenResult | null> => {
  if (!confirmationToken) return null

  return (async () => {
    const hasAuth = Boolean(authToken())
    let priorConfirmed = false

    if (hasAuth) {
      try {
        priorConfirmed = Boolean((await checkAuth())?.data?.confirmed)
      } catch {
        priorConfirmed = false
      }
    }

    await confirmEmail(confirmationToken)

    if (!hasAuth) {
      successToast({ title: "Email confirmed" })

      return null
    }

    const { data } = await checkAuth()
    const nowConfirmed = Boolean(data?.confirmed)

    if (nowConfirmed) {
      successToast({
        title: priorConfirmed ? "Email already confirmed" : "Email confirmed",
      })
    } else {
      errorToast({ title: "Unable to confirm your email" })
    }

    setCurrentUser(queryClient, data)

    return { data, priorConfirmed }
  })().catch(async () => {
    const hasAuth = Boolean(authToken())

    if (!hasAuth) {
      return null
    }

    try {
      const { data } = await checkAuth()
      const priorConfirmed = Boolean(data?.confirmed)

      if (priorConfirmed) {
        successToast({ title: "Email already confirmed" })
      }

      setCurrentUser(queryClient, data)

      return { data, priorConfirmed }
    } catch {
      return null
    }
  })
}

export default confirmEmailTokenAndToast
